/**
 * Conjunto autoral de ícones.
 *
 * Desenhados em grade de 20px, traço de 1.5px, cantos vivos e terminais
 * arredondados — o mesmo vocabulário da tipografia escolhida. Não há
 * dependência de biblioteca (nem Lucide, que é o default do shadcn e entregaria
 * exatamente o "visual de template" que o projeto evita) e não há emoji em
 * lugar nenhum da interface.
 *
 * Os ícones são decorativos por padrão (`aria-hidden`). Quando um ícone carrega
 * significado sozinho, passe `titulo` e ele vira `img` com nome acessível.
 */

export type NomeIcone =
  | "arquivo" | "baixar" | "busca" | "calendario" | "cadeado"
  | "cancelar" | "checado" | "circulo" | "copiar" | "escudo" | "esquerda"
  | "direita" | "editar" | "externo" | "filtro" | "informacao" | "lista"
  | "megafone" | "pessoas" | "planilha" | "relogio" | "selo" | "alerta"
  | "menu" | "fechar";

const TRACOS: Record<NomeIcone, React.ReactNode> = {
  arquivo: <><path d="M11.5 2.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5z" /><path d="M11.5 2.5v4h4" /></>,
  baixar: <><path d="M10 3v9" /><path d="m6.5 8.5 3.5 3.5 3.5-3.5" /><path d="M4 15.5h12" /></>,
  busca: <><circle cx="9" cy="9" r="5.5" /><path d="m13.2 13.2 3.3 3.3" /></>,
  calendario: <><rect x="3" y="4.5" width="14" height="12.5" rx="1.5" /><path d="M3 8.5h14" /><path d="M6.5 2.5v3M13.5 2.5v3" /></>,
  cadeado: <><rect x="4" y="8.5" width="12" height="8.5" rx="1.5" /><path d="M6.8 8.5V6.2a3.2 3.2 0 0 1 6.4 0v2.3" /></>,
  cancelar: <><circle cx="10" cy="10" r="7.5" /><path d="m7.4 7.4 5.2 5.2M12.6 7.4l-5.2 5.2" /></>,
  checado: <><circle cx="10" cy="10" r="7.5" /><path d="m6.6 10.2 2.4 2.4 4.4-4.8" /></>,
  circulo: <circle cx="10" cy="10" r="7.5" />,
  copiar: <><rect x="7" y="7" width="9.5" height="9.5" rx="1.5" /><path d="M13 7V5a1.5 1.5 0 0 0-1.5-1.5H5A1.5 1.5 0 0 0 3.5 5v6.5A1.5 1.5 0 0 0 5 13h2" /></>,
  escudo: <><path d="M10 2.5 4 5v5.5c0 3.4 2.5 6.2 6 7 3.5-.8 6-3.6 6-7V5z" /></>,
  esquerda: <><path d="M16 10H4" /><path d="m8.5 5.5-4.5 4.5 4.5 4.5" /></>,
  direita: <><path d="M4 10h12" /><path d="m11.5 5.5 4.5 4.5-4.5 4.5" /></>,
  editar: <><path d="M13.2 3.6a1.8 1.8 0 0 1 2.5 2.5L7.3 14.5 3.5 16l1.5-3.8z" /><path d="m12 4.8 2.5 2.5" /></>,
  externo: <><path d="M11.5 3.5H16v4.5" /><path d="M16 3.5 9 10.5" /><path d="M13.5 11.5V15a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 15V8A1.5 1.5 0 0 1 5 6.5h3.5" /></>,
  filtro: <><path d="M3.5 4.5h13l-5 6v5l-3 1.5v-6.5z" /></>,
  informacao: <><circle cx="10" cy="10" r="7.5" /><path d="M10 9v4.5" /><path d="M10 6.4h.01" /></>,
  lista: <><path d="M7 5.5h9.5M7 10h9.5M7 14.5h9.5" /><path d="M3.6 5.5h.01M3.6 10h.01M3.6 14.5h.01" /></>,
  megafone: <><path d="M4 8v4a1.5 1.5 0 0 0 1.5 1.5H7l6 3.5V4.5L7 8H5.5A1.5 1.5 0 0 0 4 8z" /><path d="M15.5 7.5a3.5 3.5 0 0 1 0 5" /></>,
  pessoas: <><circle cx="8" cy="7" r="3" /><path d="M2.5 16.5a5.5 5.5 0 0 1 11 0" /><path d="M13.5 4.4a3 3 0 0 1 0 5.2" /><path d="M14.8 11.6a5.5 5.5 0 0 1 2.7 4.9" /></>,
  planilha: <><rect x="3" y="4" width="14" height="13" rx="1.5" /><path d="M3 8.2h14M8 8.2V17M12.5 8.2V17" /></>,
  relogio: <><circle cx="10" cy="10" r="7.5" /><path d="M10 5.8V10l3 1.8" /></>,
  selo: <><path d="m10 2.5 2.1 1.6 2.6-.3.8 2.5 2.2 1.5-1.2 2.3 1.2 2.3-2.2 1.5-.8 2.5-2.6-.3L10 17.7l-2.1-1.6-2.6.3-.8-2.5L2.3 12l1.2-2.3L2.3 7.4l2.2-1.5.8-2.5 2.6.3z" /><path d="m7.6 10.1 1.8 1.8 3.2-3.6" /></>,
  menu: <><path d="M3.5 5.5h13M3.5 10h13M3.5 14.5h13" /></>,
  fechar: <><path d="m5 5 10 10M15 5 5 15" /></>,
  alerta: <><path d="M10 2.8 18 16.5H2z" /><path d="M10 8v3.6" /><path d="M10 14.2h.01" /></>,
};

type Props = {
  nome: NomeIcone;
  tamanho?: number;
  /** Quando preenchido, o ícone deixa de ser decorativo e ganha nome acessível. */
  titulo?: string;
  className?: string;
};

export function Icon({ nome, tamanho = 20, titulo, className }: Props) {
  const acessivel = Boolean(titulo);
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={acessivel ? "img" : undefined}
      aria-hidden={acessivel ? undefined : true}
      aria-label={titulo}
      focusable="false"
    >
      {TRACOS[nome]}
    </svg>
  );
}

/** Ícone correspondente a cada situação da inscrição. */
export const ICONE_SITUACAO: Record<string, NomeIcone> = {
  RASCUNHO: "editar",
  RECEBIDA: "circulo",
  EM_ANALISE: "relogio",
  DEFERIDA: "checado",
  INDEFERIDA: "cancelar",
  CLASSIFICADA: "lista",
  CONVOCADA: "megafone",
  CONTRATADA: "selo",
  ELIMINADA: "cancelar",
};
